import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
  is_active: boolean;
}

interface SurveyQuestion {
  id: string;
  text: string;
  type: 'multiple_choice' | 'text' | 'rating' | 'yes_no';
  options?: string[];
  required: boolean;
}

export default function PublicSurveyPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchSurvey();
  }, [shareId]);

  const fetchSurvey = async () => {
    if (!shareId) {
      setError('Geçersiz anket bağlantısı');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('surveys')
        .select('*')
        .eq('share_link_id', shareId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setError('Anket bulunamadı');
      } else if (!data.is_active) {
        setError('Bu anket artık aktif değil');
      } else {
        setSurvey(data);
      }
    } catch (err) {
      console.error('Anket yüklenirken hata:', err);
      setError('Anket yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: string | number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!survey) return;

    // Zorunlu soruları kontrol et
    const requiredQuestions = survey.questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => !answers[q.id]);

    if (missingAnswers.length > 0) {
      alert('Lütfen tüm zorunlu soruları yanıtlayın');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('survey_responses')
        .insert({
          survey_id: survey.id,
          respondent_email: respondentEmail || 'Belirtilmedi',
          respondent_name: respondentName || 'Anonim',
          answers: answers,
          respondent_type: 'external'
        });

      if (error) throw error;

      setSubmitted(true);
    } catch (err) {
      console.error('Anket gönderilirken hata:', err);
      alert('Anket gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-purple-500 animate-spin"></i>
          <p className="text-gray-600 mt-4 text-lg">Anket yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-red-600"></i>
          </div>
          <h2 className="text-gray-900 font-bold text-2xl mb-2">Anket Bulunamadı</h2>
          <p className="text-gray-600 text-sm">{error || 'Bu anket mevcut değil veya kaldırılmış'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
          </div>
          <h2 className="text-gray-900 font-bold text-2xl mb-2">Teşekkür Ederiz!</h2>
          <p className="text-gray-600 text-sm mb-6">Anket yanıtınız başarıyla kaydedildi. Görüşleriniz bizim için çok değerli.</p>
          <div className="w-16 h-16 flex items-center justify-center mx-auto">
            <i className="ri-heart-fill text-5xl text-pink-500 animate-pulse"></i>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/1d879113e6ca78ca5c59bde478f0d339.png"
            alt="Kartvizitler Logo"
            className="h-16 object-contain"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-8 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-survey-line text-2xl"></i>
              </div>
              <h1 className="text-white font-bold text-3xl">{survey.title}</h1>
            </div>
            {survey.description && (
              <p className="text-white/90 text-sm leading-relaxed">{survey.description}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            {/* Respondent Info */}
            <div className="bg-purple-50 rounded-xl p-6 mb-6 border border-purple-100">
              <h3 className="text-gray-900 font-semibold text-lg mb-4 flex items-center gap-2">
                <i className="ri-user-line text-purple-500"></i>
                Bilgileriniz (Opsiyonel)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium text-sm mb-2">
                    Adınız
                  </label>
                  <input
                    type="text"
                    value={respondentName}
                    onChange={(e) => setRespondentName(e.target.value)}
                    placeholder="Adınızı girin"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium text-sm mb-2">
                    E-posta Adresiniz
                  </label>
                  <input
                    type="email"
                    value={respondentEmail}
                    onChange={(e) => setRespondentEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {survey.questions.map((question, index) => (
                <div key={question.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <label className="block text-gray-900 font-medium mb-4">
                    {index + 1}. {question.text}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </label>

                  {question.type === 'multiple_choice' && question.options && (
                    <div className="space-y-2">
                      {question.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 cursor-pointer transition-all"
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option}
                            checked={answers[question.id] === option}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            className="w-5 h-5 text-purple-600 cursor-pointer"
                            required={question.required}
                          />
                          <span className="text-gray-700 text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'text' && (
                    <textarea
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      rows={4}
                      placeholder="Yanıtınızı buraya yazın..."
                      required={question.required}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                    />
                  )}

                  {question.type === 'rating' && (
                    <div>
                      <div className="flex items-center justify-center gap-2 py-4">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => handleAnswerChange(question.id, star)}
                            className="p-2 cursor-pointer transition-all hover:scale-125"
                          >
                            <i className={`text-5xl ${
                              star <= (answers[question.id] || 0)
                                ? 'ri-star-fill text-amber-400'
                                : 'ri-star-line text-gray-300'
                            }`}></i>
                          </button>
                        ))}
                      </div>
                      {answers[question.id] && (
                        <p className="text-center text-gray-600 text-sm font-medium mt-2">
                          Seçilen puan: {answers[question.id]}/5
                        </p>
                      )}
                    </div>
                  )}

                  {question.type === 'yes_no' && (
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleAnswerChange(question.id, 'Evet')}
                        className={`flex-1 px-6 py-4 rounded-lg font-medium transition-all cursor-pointer ${
                          answers[question.id] === 'Evet'
                            ? 'bg-green-500 text-white shadow-lg scale-105'
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <i className="ri-checkbox-circle-line mr-2"></i>
                        Evet
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnswerChange(question.id, 'Hayır')}
                        className={`flex-1 px-6 py-4 rounded-lg font-medium transition-all cursor-pointer ${
                          answers[question.id] === 'Hayır'
                            ? 'bg-red-500 text-white shadow-lg scale-105'
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <i className="ri-close-circle-line mr-2"></i>
                        Hayır
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold text-lg rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-xl"></i>
                    Anketi Gönder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            Görüşleriniz bizim için çok değerli. Teşekkür ederiz! 💜
          </p>
        </div>
      </div>
    </div>
  );
}
